<template>
  <div :class="'md-layout'">
    <div :class="'md-layout-item md-size-100'">
      <md-card>
        <md-card-header :class="'md-card-header-icon md-card-header-danger'">
          <div :class="'card-icon'">
            <md-icon>assignment</md-icon>
          </div>
          <h4 :class="'title'">
            List
            <md-button :class="'md-danger pull-right'" @click.prevent="isTryingToImport = true" 
            v-if="cannotView() && team.length > 0">
              Import
            </md-button>
          </h4>
        </md-card-header>
        <md-card-content>
          <!--Filter-->
          <div :class="'md-layout'">
            <div :class="'md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33 md-large-size-33'">
              <md-field>
                <label>Email</label>
                <md-input placeholder="Email" v-model="filter.email" @input="getUsers" />
              </md-field>
            </div>
          </div>

          <!--Table-->
          <md-table v-model="team" table-header-color="orange">
            <md-empty-state md-icon="list" md-label="Users Admin" :md-description=" 'No members of Users Admin found' +
                (this.filtersQuery ? ' with the searched terms' : '')" v-if="!isFetching" >
              <md-button v-if="cannotView()" :class="'md-primary md-raised'" @click.prevent="isTryingToImport = true">
                Import
              </md-button>
            </md-empty-state>
            <md-table-row slot="md-table-row" slot-scope="{ item: user }">
              <md-table-cell md-label="Name">{{ user.name }}</md-table-cell>
              <md-table-cell md-label="Email">{{ user.email }}</md-table-cell>
              <md-table-cell md-label="Position">{{ user.position }}</md-table-cell>
              <md-table-cell md-label="User type">{{ user.roles.map((role) => role.name).join(", ") }}</md-table-cell>
              <md-table-cell v-if="cannotView()" md-label="Actions" md-numeric>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" 
                  @click.prevent="tryToEdit(user)" title="Edit team member">
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" @click.prevent="tryToActiveInactive(user)"
                  :title=" user.inactive_user === null ? 'Inactive team member' : 'Active team member'">
                  <md-icon>{{ user.inactive_user !== null ? "lock" : "lock_open" }}</md-icon>
                </md-button>
                <md-button v-if="!user.inactive_user && noPermissionToDelete(user)" :class="'md-simple md-just-icon'"
                  :md-ripple="false" @click.prevent="tryToDelete(user)" title="Delete team member">
                  <md-icon>delete</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
          <div :class="'md-flex text-center'" v-if="team.length > 0">
            <Pagination :total="pagination.totalItems" :value="pagination.currentPage" 
              :perPage="pagination.perPage" @input="changePage"/>
          </div>
          <div :class="'md-flex text-center'" v-if="isFetching">
            <md-progress-spinner :class="'md-accent'" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <md-dialog :md-active.sync="isTryingToActive">
      <md-dialog-title>{{ activeUser.inactive_user ? "Active" : "Inactive" }} team member</md-dialog-title>
      <md-dialog-content>
        Are you sure you want to {{ activeUser.inactive_user ? "active" : "inactive" }}
        <b>{{ activeUser.name }} ({{ activeUser.email }})</b> from the team?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isActivatingUser" @click.prevent="dismissActiveModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isActivatingUser" @click.prevent="activeInactiveUser(activeUser)">
          {{ !isDeletingUser ? activeUser.inactive_user ? "Active" : "Inactive" : "Changing status..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="isTryingToDelete">
      <md-dialog-title>Delete team member</md-dialog-title>
      <md-dialog-content>
        Are you sure you want to delete
        <b>{{ deletableUser.name }} ({{ deletableUser.email }})</b> from the team?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isDeletingUser" @click.prevent="dismissDeleteModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isDeletingUser" @click.prevent="deleteUser(deletableUser)">
          {{ !isDeletingUser ? "Delete" : "Deleting..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="isTryingToImport">
      <md-dialog-title>Import team members</md-dialog-title>
      <md-dialog-content>
        It is possible to import team members via spreadsheet file.
        <md-field>
          <label>Upload file</label>
          <md-file :disabled="isImportingUsers" placeholder="Spreadsheet file" accept=".xls, .xlsx" @md-change="chooseFileToImport"/>
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isImportingUsers" @click.prevent="dismissImportationModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isImportingUsers || !selectedFile" @click.prevent="importUsers">
          {{ !isImportingUsers ? "Import" : "Importing..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="isTryingToEdit">
      <md-dialog-title>Edit team member</md-dialog-title>
      <md-dialog-content>
        <div :class="'md-layout'">
          <div :class="'md-layout-item md-size-100'">
            <md-field :class="$v.editableUser.name.$invalid ? 'md-error' : ''">
              <label for="name">Name</label>
              <md-input id="name" v-model="editableUser.name" :disabled="isEditingUser"/>
              <span :class="'md-error'" v-if="!$v.editableUser.name.required">The first name is required</span>
              <span :class="'md-error'" v-else-if="!$v.editableUser.name.maxLength">Invalid first name</span>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field :class="$v.editableUser.email.$invalid ? 'md-error' : ''">
              <label for="email">Email</label>
              <md-input id="email" v-model="editableUser.email" :disabled="isEditingUser" />
              <span :class="'md-error'" v-if="!$v.editableUser.email.required">The last name is required</span>
              <span :class="'md-error'" v-else-if="!$v.editableUser.email.maxLength" >Invalid last name</span>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">User Type</label>
              <md-select v-model="editableUserRoles" id="user-type">
                <md-option :value="1">Admin</md-option>
                <md-option :value="2">User</md-option>
              </md-select>
            </md-field>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isEditingUser" @click.prevent="dismissEditModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="$v.editableUser.$invalid || isEditingUser" @click.prevent="editUser">
          {{ !isEditingUser ? "Update" : "Updating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { email, maxLength, required } from "vuelidate/lib/validators";
import { date, rqrType } from "@/filters";
import { Pagination } from "@/components";
import { camelToSnake } from "@/methods/api/requests";
import * as utils from "@/utils";
import * as filters from "@/filters";

export default {
  components: {
    Pagination,
  },

  filters: {
    date,
    rqrType,
  },

  data() {
    return {
      team: [],
      isFetching: false,
      isTryingToActive: false,
      activeUser: {},
      isActivatingUser: false,
      isTryingToDelete: false,
      deletableUser: {},
      isDeletingUser: false,
      isTryingToImport: false,
      selectedFile: null,
      isImportingUsers: false,
      isTryingToEdit: false,
      editableUser: {},
      editableUserRoles: null,
      isEditingUser: false,
      filter: {
        email: "",
      },
      pagination: {
        totalItems: "",
        currentPage: "",
        perPage: "",
      },
      usersDebounceTimer: null,
      errorResponses: {
        400: "The users spreadsheet is invalid",
        422: "There is an error with your file",
      },
    };
  },

  validations: {
    editableUser: {
      name: {
        required,
        maxLength: maxLength(254),
      },
      email: {
        required,
        email,
      },
    },
  },

  computed: {
    filtersQuery() {
      return filters.getFiltersQuery(this.filter);
    },

    paginationQuery() {
      return utils.getPaginationQuery(this.pagination.currentPage);
    },
  },

  methods: {
    cannotView() {
      return utils.getCannotView(this.$store.state.currentProfile)
    },

    tryToEdit(user) {
      this.isTryingToEdit = true;
      this.editableUser = Object.assign({}, user);
      this.editableUserRoles = user.roles[0].id;
    },

    dismissEditModal() {
      if (this.isEditingUser)
        return;
      
      this.isTryingToEdit = false;
    },

    editUser() {
      if (this.isEditingUser)
        return;      

      this.isEditingUser = true;
      const edittedUser = {
        name: this.editableUser.name,
        email: this.editableUser.email,
        role: this.editableUserRoles,
      };

      this.$api.request
        .put(`users/${this.editableUser.id}`, edittedUser)
        .then(() => {
          this.team = [];
          this.editableUserRoles = null;
          this.isEditingUser = false;
          this.dismissEditModal();
          this.$toast.show(
            `<b>${this.editableUser.name}</b> was updated successfully`,
            "Team member updated successfully"
          );

          this.getUsers();
        })
        .catch((error) => {
          if (this.userErrorResponses[error.response.status]) {
            this.$toast.show(this.userErrorResponses[error.response.status]);
          } else {
            this.$toast.show("Ops, something went wrong! Please, try again.");
          }
        })
        .finally(() => {
          this.isEditingUser = false;
        });
    },

    tryToDelete(user) {
      this.isTryingToDelete = true;
      this.deletableUser = user;
    },

    dismissDeleteModal() {
      if (this.isDeletingUser)
        return;
      
      this.isTryingToDelete = false;
    },

    deleteUser() {
      if (this.isDeletingUser)
        return;      

      this.isDeletingUser = true;
      this.$api.request
        .delete(`users/${this.deletableUser.id}`)
        .then((response) => {
          if (response.status === 200) {
            this.team = [];

            this.isDeletingUser = false;
            this.dismissDeleteModal();
            this.$toast.show(
              `<b>${this.deletableUser.name}</b> was deleted successfully`,
              "Team member deleted successfully"
            );

            this.getUsers();
          }
        })
        .finally(() => {
          this.isDeletingUser = false;
        });
    },

    tryToActiveInactive(user) {
      this.isTryingToActive = true;
      this.activeUser = user;
    },

    dismissActiveModal() {
      if (this.isActivatingUser)
        return;
      
      this.isTryingToActive = false;
    },

    activeInactiveUser() {
      if (this.isActivatingUser)
        return;      

      this.isActivatingUser = true;
      this.$api.request
        .put(`users/${this.activeUser.id}/inactive-user`)
        .then((response) => {
          if (response.status === 200) {
            this.team = [];
            this.isActivatingUser = false;
            this.dismissActiveModal();
            this.$toast.show(
              `<b>${this.activeUser.name}</b> was activated / inactivated successfully`,
              "Team member activated / inactivated successfully"
            );
            this.getUsers();
          }
        })
        .finally(() => {
          this.isActivatingUser = false;
        });
    },

    dismissImportationModal() {
      if (this.isImportingUsers)
        return;      

      this.isTryingToImport = false;
      this.selectedFile = null;
    },

    chooseFileToImport(fileList) {
      this.selectedFile = fileList[0];
    },

    importUsers() {
      if (this.isImportingUsers) 
        return;      

      this.isImportingUsers = true;
      const formData = new FormData();
      formData.append("spreadsheet", this.selectedFile);

      this.$api.request
        .post("users/import", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          this.team = [];
          this.isImportingUsers = false;
          this.dismissImportationModal();
          this.$toast.show("Team members have been imported");
          this.getUsers();
        })
        .catch((error) => {
          const message = error.response.data.message;
          if (message)
            this.$toast.show(message);
          else if (this.errorResponses[error.response.status])
            this.$toast.show(this.errorResponses[error.response.status]);
          else
            this.$toast.show("Ops, something went wrong! Please, try again.");
          
        })
        .finally(() => {
          this.isImportingUsers = false;
        });
    },

    getUsers() {
      this.isFetching = true;
      this.$api
        .request(`users?${this.filtersQuery}${this.paginationQuery}`)
        .then((response) => {
          this.team = camelToSnake(response.data.data);
          this.pagination.totalItems = response.data.meta.total;
          this.pagination.currentPage = response.data.meta.currentPage;
          this.pagination.perPage = response.data.meta.perPage;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    changePage(toPage) {
      if (toPage === this.pagination.currentPage)
        return;
      
      this.pagination.currentPage = toPage;
      this.getUsers();
    },

    noPermissionToDelete(user) {
      return !(
        user &&
        ((user.userEngagemantLeander && user.userEngagemantLeander.length) ||
          (user.userManagerEngagemant && user.userManagerEngagemant.length) ||
          (user.userRqrReviewers && user.userRqrReviewers.length) ||
          (user.userdirectorId && user.userdirectorId.length))
      );
    },
  },

  created() {
    this.getUsers();
  },
};
</script>
